/**
 * Slider Component
 *
 * A range input component with optional label and value display.
 * Features a custom styled thumb and track matching the Brilliant design system.
 *
 * @example
 * ```tsx
 * <Slider
 *   value={hours}
 *   onChange={setHours}
 *   min={0}
 *   max={8}
 *   step={0.5}
 *   label="Hours"
 * />
 * ```
 */

import classNames from "classnames";
import React, { useCallback, useId, useMemo } from "react";
import styles from "./Slider.module.css";
import type { SliderProps } from "./types";

export const Slider = React.forwardRef<HTMLInputElement, SliderProps>(
  (
    {
      value,
      onChange,
      min = 0,
      max = 100,
      step = 1,
      label,
      showValue = true,
      formatValue = (v) => String(v),
      disabled = false,
      size = "md",
      variant = "dark",
      className,
      "data-testid": testId,
      "aria-label": ariaLabel,
      "aria-describedby": ariaDescribedBy,
    },
    ref,
  ) => {
    const sliderId = useId();
    const labelId = label ? `${sliderId}-label` : undefined;
    const valueId = `${sliderId}-value`;

    // Calculate the percentage for positioning the value indicator
    const percent = useMemo(() => {
      return ((value - min) / (max - min)) * 100;
    }, [value, min, max]);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(Number(event.target.value));
      },
      [onChange],
    );

    // Calculate the thumb position for the value tooltip
    // The thumb doesn't reach the very edges - it stays inset by half its width (0.25rem)
    // So we interpolate between 0.25rem (at 0%) and calc(100% - 0.25rem) (at 100%)
    // CSS transform: translateX(-50%) handles centering the indicator on this position
    const thumbPosition = `calc(${percent}% + ${0.25 - (percent / 100) * 0.5}rem)`;

    return (
      <div
        className={classNames(
          styles.container,
          styles[variant],
          { [styles.disabled]: disabled },
          className,
        )}
        data-size={size}
        data-testid={testId}
      >
        {label && (
          <label id={labelId} htmlFor={sliderId} className={styles.label}>
            {label}
          </label>
        )}

        <div className={styles.trackContainer}>
          <input
            ref={ref}
            id={sliderId}
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            aria-label={ariaLabel || label}
            aria-labelledby={!ariaLabel && label ? labelId : undefined}
            aria-describedby={ariaDescribedBy}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={value}
            aria-valuetext={formatValue(value)}
            className={classNames(styles.input, {
              [styles.inputDisabled]: disabled,
            })}
            data-testid={testId ? `${testId}-input` : undefined}
          />

          {showValue && (
            <div
              id={valueId}
              className={styles.valueIndicator}
              style={{ left: thumbPosition }}
              aria-hidden="true"
            >
              <span className={styles.valueText}>{formatValue(value)}</span>
            </div>
          )}
        </div>

        {/* Min/Max labels for accessibility */}
        <div className={styles.minMaxLabels}>
          <span aria-hidden="true">{formatValue(min)}</span>
          <span aria-hidden="true">{formatValue(max)}</span>
        </div>
      </div>
    );
  },
);

Slider.displayName = "Slider";
